import './lib/env'

import { Toaster } from '@/components/ui/sonner'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { LazyMotion, domAnimation } from 'motion/react'
import { ThemeProvider } from 'next-themes'
import { StrictMode, Suspense, lazy } from 'react'
import type { JSX } from 'react'
import { createRoot } from 'react-dom/client'

import { env } from '@/lib/env'
import { queryClient } from '@/lib/query-client'
import { routeTree } from './routeTree.gen'
import './styles/globals.css'

// ── Chunk load failures (ej. nuevo deploy con hashes distintos) ──────────────
// Vite emite este evento cuando un import() dinámico falla. Sin este handler
// la app queda en pantalla en blanco sin posibilidad de recuperarse.
window.addEventListener('vite:preloadError', () => {
  window.location.reload()
})

// ── Restauración desde bfcache (Back/Forward Cache) ─────────────────────────
// iOS Safari y navegadores de escritorio congelan pestañas en segundo plano y
// las restauran con bfcache. React + Zustand pueden quedar en estado
// inconsistente tras este proceso, resultando en pantalla en blanco.
// Solución: si la pestaña fue ocultada (app switch, no navegación Back) antes
// de la restauración, recargamos para garantizar un estado limpio.
let hiddenAt: number | null = null

document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    hiddenAt = Date.now()
  } else {
    hiddenAt = null
  }
})

window.addEventListener('pageshow', (event: PageTransitionEvent) => {
  // event.persisted = true  →  página restaurada desde bfcache (no recarga normal)
  // hiddenAt !== null       →  la página fue ocultada (switch de app), no navegación Back
  if (event.persisted && hiddenAt !== null) {
    window.location.reload()
  }
})

// ── Devtools: carga lazy para no bloquear el bundle inicial ─────────────────
// En producción, el tree-shaking de Vite elimina estas líneas completamente
// porque env.VITE_ENVIRONMENT === 'develop' se evalúa a false en build time.
// En develop, se cargan como chunks separados después de que React renderiza.
const ReactQueryDevtools =
  env.VITE_ENVIRONMENT === 'develop'
    ? lazy(() =>
        import('@tanstack/react-query-devtools').then((m) => ({
          default: m.ReactQueryDevtools,
        })),
      )
    : null

// ── Pending component para rutas en estado de carga ──────────────────────────
// Sin esto, si el router entra en estado `pending` (ej. re-ejecutando beforeLoad)
// la pantalla queda en blanco. Este spinner reemplaza ese vacío.
function RouterPendingFallback(): JSX.Element {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center">
      <div className="size-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // 300 ms antes de disparar el pre-carga al hacer hover sobre un link.
  // El default (50 ms) es demasiado agresivo y causa 401s en la consola
  // al pasar el cursor sobre links en rutas públicas (/_public beforeLoad).
  defaultPreloadDelay: 300,
  // Mostrar spinner si la carga toma más de 200 ms (equilibrio entre flash y feedback).
  // Las rutas públicas tienen beforeLoad síncrono → nunca llegan a 200 ms.
  // Las rutas protegidas con refresh de token (~300-800 ms) sí muestran el spinner.
  defaultPendingComponent: RouterPendingFallback,
  defaultPendingMs: 200,
  defaultPendingMinMs: 300,
})

// Registrar el tipo del router para type safety end-to-end con TanStack Router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Elemento #root no encontrado en el DOM')

createRoot(rootElement).render(
  <StrictMode>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      {/* LazyMotion carga solo domAnimation (~4.6 KB) en lugar del bundle completo de Motion */}
      <LazyMotion features={domAnimation} strict>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          {/* top-center evita el notch del iPhone en landscape. top-right queda oculto tras él. */}
          <Toaster richColors />
          {ReactQueryDevtools !== null && (
            <Suspense>
              <ReactQueryDevtools initialIsOpen={false} />
            </Suspense>
          )}
        </QueryClientProvider>
      </LazyMotion>
    </ThemeProvider>
  </StrictMode>,
)
